/* GrowthOS — Ticket form sections (used by addTasksForm.jsx for Create/Edit).
   Rebuilt config-driven on the shared gos-cl-* / gos-dd-* primitives (like
   leadForm/dealForm). The two dependent selectors are preserved from the legacy
   form: "Ticket For" (Lead|Contact → a searchable record dropdown) and
   "Associated With" (Account|Opportunity|Campaign → a searchable record dropdown,
   disabled when Ticket For = Lead). All the wiring stays in the container's
   handleChange → handleLookupDropDownChange (ticketId/associateId reset the sibling
   record fields exactly as before); this file only renders + forwards. No view
   mode — view pages use viewTasks.jsx's read-only shell. */
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getActiveOwners, getGenericMasterFilteredListByKey } from '../../redux/selector';
import { NameFormatter } from '../../shared/controls/formatters';
import PaginatedDropdown from '../../shared/component/PaginatedDropdown';
import apiServices from '../../services/service';
import { FIc } from "../../shared/component/GosIcon";


const Select = ({ value, placeholder, options, onChange, disabled, searchable, onSearch, onScroll, loading }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const onListScroll = (e) => {
    if (!onScroll) return;
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) onScroll();
  };
  return (
    <div className="gos-dd" ref={ref}>
      <button type="button" className={'gos-dd-btn' + (open ? ' open' : '') + (value ? ' has-val' : '')} onClick={() => !disabled && setOpen((o) => !o)} disabled={disabled}>
        <span className="gos-dd-label">{value || placeholder || 'Select…'}</span>
        <FIc name="chevD" size={14} style={{ flex: 'none', color: 'var(--gos-text-3)' }} />
      </button>
      {open ? (
        <div className="gos-dd-pop">
          {searchable ? (
            <div className="gos-dd-search">
              <FIc name="search" size={14} />
              <input autoFocus placeholder="Search…" onChange={(e) => onSearch && onSearch(e.target.value)} />
            </div>
          ) : null}
          <div className="gos-dd-list" onScroll={onScroll ? onListScroll : undefined}>
            {loading && options.length === 0 ? <div className="gos-dd-empty">Loading…</div> : null}
            {!loading && options.length === 0 ? <div className="gos-dd-empty">No options</div> : null}
            {options.map((o) => (
              <button key={o.value} type="button" className={'gos-dd-item' + (o.label === value ? ' on' : '')} onClick={() => { onChange(o); setOpen(false); }}>
                <span className="gos-dd-grow">{o.label}</span>
                {o.label === value ? <FIc name="check" size={14} style={{ color: 'var(--gos-accent)' }} /> : null}
              </button>
            ))}
            {loading && options.length > 0 ? <div className="gos-dd-empty">Loading more…</div> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const opt = (list, vKey, lKey, fmt) => (list || []).map((o) => ({ value: o[vKey], label: fmt ? fmt(o[lKey]) : o[lKey] }));

const Field = ({ label, req, children, err, full }) => (
  <div className={'gos-cl-field' + (full ? ' gos-cl-field-full' : '')}>
    <label className="gos-cl-label">
      {label}
      {req ? <span className="gos-cl-req">*</span> : null}
    </label>
    {children}
    {err ? <span className="gos-cl-err">{err}</span> : null}
  </div>
);

const TaskForm = ({ formData, handleSubmit, handleChangeForm, handleChange, errormsg }) => {
  const owner = useSelector(getActiveOwners);
  const ticketStatusDropdown = useSelector(getGenericMasterFilteredListByKey('StatusCrm'));
  const ticketPriorityDropdown = useSelector(getGenericMasterFilteredListByKey('TicketPriorityCrm'));

  const {
    options: leadListDropdown, isLoading: isLeadLoading, handleSearch: onLeadSearch, handleScroll: onLeadScroll,
  } = PaginatedDropdown({ fetchFn: apiServices.getLeadsDropdown, responseKey: 'leadList' });
  const {
    options: contactListDropdown, isLoading: isContactLoading, handleSearch: onContactSearch, handleScroll: onContactScroll,
  } = PaginatedDropdown({ fetchFn: apiServices.getContactsDropdown, responseKey: 'contactList' });
  const {
    options: accountListDropdown, isLoading: isAccountLoading, handleSearch: onAccountSearch, handleScroll: onAccountScroll,
  } = PaginatedDropdown({ fetchFn: apiServices.getAccountsDropdown, responseKey: 'accountList' });
  const {
    options: campaignListDropdown, isLoading: isCampaignLoading, handleSearch: onCampaignSearch, handleScroll: onCampaignScroll,
  } = PaginatedDropdown({ fetchFn: apiServices.getCampaignDropdown, responseKey: 'campaignList' });
  const {
    options: opportunityListDropdown, isLoading: isOpportunityLoading, handleSearch: onOpportunitySearch, handleScroll: onOpportunityScroll,
  } = PaginatedDropdown({ fetchFn: apiServices.getOpportunityDropdown, responseKey: 'opportunityList' });

  const isLead = formData?.ticket === 'Lead';

  const sectionCard = (icon, title, sub, body) => (
    <div className="gos-lr-card gos-cl-sec">
      <div className="gos-cl-sec-head">
        <span className="gos-cl-sec-ic"><FIc name={icon} size={17} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="gos-cl-sec-title">{title}</div>
          <div className="gos-cl-sec-sub">{sub}</div>
        </div>
      </div>
      {body}
    </div>
  );

  // ---- "Ticket For" dependent record dropdown ----
  const ticketForRecord = () => {
    if (isLead) {
      return (
        <Select
          value={formData?.leadName || ''}
          placeholder="Select Lead"
          options={opt(leadListDropdown, 'id', 'fullName')}
          onChange={(o) => handleChange(o, 'leadId')}
          searchable
          onSearch={onLeadSearch}
          onScroll={onLeadScroll}
          loading={isLeadLoading}
        />
      );
    }
    return (
      <Select
        value={formData?.contactName || ''}
        placeholder="Select Contact"
        options={opt(contactListDropdown, 'id', 'name')}
        onChange={(o) => handleChange(o, 'contactId')}
        searchable
        onSearch={onContactSearch}
        onScroll={onContactScroll}
        loading={isContactLoading}
      />
    );
  };

  // ---- "Associated With" dependent record dropdown ----
  const associateRecord = () => {
    const a = formData?.associate;
    if (a === 'Campaign') {
      return (
        <Select value={formData?.campaignName || ''} placeholder="Select Campaign" options={opt(campaignListDropdown, 'id', 'campaignName')} onChange={(o) => handleChange(o, 'campaignId')} searchable onSearch={onCampaignSearch} onScroll={onCampaignScroll} loading={isCampaignLoading} disabled={isLead} />
      );
    }
    if (a === 'Opportunity') {
      return (
        <Select value={formData?.opportunityName || ''} placeholder="Select Opportunity" options={opt(opportunityListDropdown, 'id', 'name')} onChange={(o) => handleChange(o, 'opportunityId')} searchable onSearch={onOpportunitySearch} onScroll={onOpportunityScroll} loading={isOpportunityLoading} disabled={isLead} />
      );
    }
    return (
      <Select value={formData?.accountName || ''} placeholder="Select Account" options={opt(accountListDropdown, 'id', 'name')} onChange={(o) => handleChange(o, 'accountId')} searchable onSearch={onAccountSearch} onScroll={onAccountScroll} loading={isAccountLoading} disabled={isLead} />
    );
  };

  return (
    <div className="gos-qf-body">
      {sectionCard('ticket', 'Ticket Information', 'Owner, status & scheduling',
        <div className="gos-cl-sec-body">
          <Field label="Ticket Owner" req err={errormsg?.ownerId}>
            <Select value={formData?.owner || ''} placeholder="Select Ticket Owner" options={opt(owner, 'userId', 'display_name', NameFormatter)} onChange={(o) => handleChange(o, 'ownerId')} />
          </Field>
          <Field label="Subject" req err={errormsg?.subject}>
            <input className={'gos-cl-input' + (errormsg?.subject ? ' err' : '')} name="subject" placeholder="Enter Subject" value={formData?.subject ?? ''} onChange={handleChangeForm} />
          </Field>
          <Field label="Status">
            <Select value={formData?.status || ''} placeholder="Select Status" options={opt(ticketStatusDropdown, 'id', 'displayName')} onChange={(o) => handleChange(o, 'statusId')} />
          </Field>
          <Field label="Priority">
            <Select value={formData?.priority || ''} placeholder="Select Priority" options={opt(ticketPriorityDropdown, 'id', 'displayName')} onChange={(o) => handleChange(o, 'priorityId')} />
          </Field>
          <Field label="Due Date" err={errormsg?.dueDate}>
            <input type="date" className={'gos-cl-input' + (errormsg?.dueDate ? ' err' : '')} name="dueDate" value={formData?.dueDate ? String(formData.dueDate).substring(0, 10) : ''} min={new Date().toISOString().split('T')[0]} onChange={handleChangeForm} />
          </Field>
          <Field label="Reminder">
            <input type="datetime-local" className="gos-cl-input" name="reminder" value={formData?.reminder ? String(formData.reminder).substring(0, 16) : ''} onChange={handleChangeForm} />
          </Field>
        </div>
      )}

      {sectionCard('link', 'Association', 'Link this ticket to a record',
        <div className="gos-cl-sec-body">
          <Field label="Ticket For">
            <Select value={formData?.ticket || ''} placeholder="Select" options={[{ value: 'lead', label: 'Lead' }, { value: 'contact', label: 'Contact' }]} onChange={(o) => handleChange(o, 'ticketId')} />
          </Field>
          <Field label={isLead ? 'Lead' : 'Contact'}>
            {ticketForRecord()}
          </Field>
          <Field label="Associated With">
            <Select value={formData?.associate || ''} placeholder="Select" options={[{ value: 'account', label: 'Account' }, { value: 'opportunity', label: 'Opportunity' }, { value: 'campaign', label: 'Campaign' }]} onChange={(o) => handleChange(o, 'associateId')} disabled={isLead} />
          </Field>
          <Field label={formData?.associate || 'Record'}>
            {associateRecord()}
          </Field>
        </div>
      )}

      {sectionCard('note', 'Description', 'Details & notes',
        <div className="gos-cl-sec-body">
          <Field label="Description" full err={errormsg?.description}>
            <textarea className={'gos-cl-input gos-cl-textarea' + (errormsg?.description ? ' err' : '')} name="description" rows={4} placeholder="Enter Description" value={formData?.description ?? ''} onChange={handleChangeForm} />
          </Field>
        </div>
      )}
    </div>
  );
};

export default TaskForm;
